import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { RouteComponentProps } from "react-router";

import { Card, CardBody } from "../components/wombat-ui/components/Card";
import Button from "../components/wombat-ui/components/Button";
import Input from "../components/wombat-ui/components/Form/Input";

import Logo from "../components/Logo";

import useTranslations from "../hooks/useTranslations";
import useAddNotification from "../hooks/useAddNotification";
import useUserLogin from "../graphql/hooks/mutations/useUserLogin";
import useSetCredential from "../graphql/hooks/mutations/useSetCredential";
import { GET_VIEWER } from "../graphql/hooks/queries/useGetViewer";

type LoginFormData = {
  email: string;
  password: string;
};

type LoginScreenScreenProps = {} & RouteComponentProps<{}>;

const LoginScreen: React.FC<LoginScreenScreenProps> = () => {
  const t = useTranslations();
  const [addNotification] = useAddNotification();
  const { register, handleSubmit, errors } = useForm<LoginFormData>();

  const [userLogin] = useUserLogin();
  const [setCredential] = useSetCredential();

  const onSubmit = handleSubmit(async ({ email, password }) => {
    try {
      const credentialData = await userLogin({
        variables: {
          email,
          password,
        },
      });

      setCredential({
        variables: credentialData.data.userLogin.credentials,
        refetchQueries: [{ query: GET_VIEWER }],
      });
    } catch (error) {
      addNotification({
        variables: {
          title: t("forms.errors.notificationTitle") as string,
          body: t("forms.errors.emailOrPassword") as string,
          status: "error",
        },
      });
    }
  });

  return (
    <div className="flex flex-col justify-center min-h-screen py-12 bg-gray-50 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-row justify-center">
          <Logo />
        </div>
        <h2 className="mt-6 text-2xl font-extrabold text-center text-dark-300">
          {t("header.connection")}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardBody>
            <form className="px-2 space-y-6" onSubmit={onSubmit} id="loginForm">
              <Input
                id="email"
                label={t("forms.fields.email")}
                name="email"
                type="email"
                autoComplete="email"
                required
                ref={register({ required: true })}
              />
              <Input
                id="password"
                label={t("forms.fields.password")}
                name="password"
                type="password"
                autoComplete="current-password"
                required
                ref={register({ required: true })}
              />

              <div className="flex items-center justify-end">
                <div className="text-sm">
                  <Link
                    to="/forgotten-password"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t("header.passwordLost")}
                  </Link>
                </div>
              </div>

              <Button type="submit" block>
                {t("forms.actions.connection")}
              </Button>
            </form>
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 text-gray-500 bg-white">
                    {t("login.or")}
                  </span>
                </div>
              </div>
              <Link
                to="/connection-link"
                className="inline-flex justify-center w-full px-4 py-2 mt-6 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                {t("login.useConnectionLink")}
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default LoginScreen;
